import CodeEditor from '../ui/CodeEditor';
import SyntaxHighlightInput from '../ui/SyntaxHighlightInput';
import HeaderKeyInput from '../ui/HeaderKeyInput';
import { KvTable, KvRow } from '../ui/KvTable';
import RequestTabBody from './RequestTabBody';
import AuthEditorPanel from '../AuthEditorPanel';
import { REQUEST_BUILDER_KV_INPUT_CLASS, REQUEST_BUILDER_KV_INPUT_STYLE } from './requestBuilderLayout';

/** Params / Headers / Auth / Scripts / Mock tab bodies (plain render function, called where the markup was). */
export function renderRequestEditorTabs({
  activeTab, params, updateParams, removeParam, t, handleParamKeyChange, selectedEnvironment,
  workspaceVariables, globalVariables, collectionVariables, folderVariables, onVariableUpdate,
  initialData, currentWorkspaceId, auth, updateAuth, collectionName, authVariableProps, headers,
  updateHeaders, removeHeader, handleHeaderKeyChange, cookies, updateCookies, removeCookie,
  handleCookieKeyChange, scriptSubTabs, setScriptSubTab, scriptSubTab, splitLayoutTick,
  preRequestScript, setPreRequestScript, notifyChange, sendHttpRequest, testScript, setTestScript,
}) {
  return (
    <RequestTabBody fixed={activeTab === 'script'}>
      {/* Params Tab */}
      {activeTab === 'params' && (
        <KvTable>
          {params.map((param, index) => (
            <KvRow
              key={param._id}
              enabled={param.enabled}
              onToggleEnabled={(v) => updateParams(index, 'enabled', v)}
              showRemove={!(index === params.length - 1 && param.key === '')}
              onRemove={() => removeParam(index)}
              removeDisabled={params.length === 1}
              renderKey={
                <SyntaxHighlightInput
                  expandOnOverflow
                  placeholder={t('request.key')}
                  value={param.key}
                  onChange={(e) => handleParamKeyChange(index, e.target.value)}
                  showDynamicVarPicker={false}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
              renderValue={
                <SyntaxHighlightInput
                  selectionToVariable
                  expandOnOverflow
                  placeholder={t('request.value')}
                  value={param.value}
                  onChange={(e) => updateParams(index, 'value', e.target.value)}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
            />
          ))}
        </KvTable>
      )}
    
      {activeTab === 'authorization' && (
        <AuthEditorPanel
          auth={auth}
          onChange={updateAuth}
          showInheritOption
          inheritSourceName={collectionName}
          variableProps={authVariableProps}
        />
      )}
    
      {/* Headers Tab */}
      {activeTab === 'headers' && (
        <KvTable>
          {/* System header: always sent, not editable */}
          <KvRow
            locked
            renderKey={<div className="system-header-cell w-full">User-Agent</div>}
            renderValue={<div className="system-header-cell w-full">Apiboo/1.0</div>}
          />
          {headers.map((header, index) => (
            <KvRow
              key={header._id}
              enabled={header.enabled}
              onToggleEnabled={(v) => updateHeaders(index, 'enabled', v)}
              checkboxTabIndex={-1}
              showRemove={!(index === headers.length - 1 && header.key === '')}
              onRemove={() => removeHeader(index)}
              removeDisabled={headers.length === 1}
              removeTabIndex={-1}
              renderKey={
                <HeaderKeyInput
                  placeholder={t('request.key')}
                  value={header.key}
                  onChange={(e) => handleHeaderKeyChange(index, e.target.value)}
                  showDynamicVarPicker={false}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
              renderValue={
                <SyntaxHighlightInput
                  selectionToVariable
                  placeholder={t('request.value')}
                  value={header.value}
                  onChange={(e) => updateHeaders(index, 'value', e.target.value)}
                  showDynamicVarPicker={false}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
            />
          ))}
        </KvTable>
      )}
    
      {/* Cookies Tab */}
      {activeTab === 'cookies' && (
        <KvTable>
          {cookies.map((cookie, index) => (
            <KvRow
              key={cookie._id}
              enabled={cookie.enabled}
              onToggleEnabled={(v) => updateCookies(index, 'enabled', v)}
              showRemove={!(index === cookies.length - 1 && cookie.key === '')}
              onRemove={() => removeCookie(index)}
              removeDisabled={cookies.length === 1}
              renderKey={
                <SyntaxHighlightInput
                  placeholder={t('request.cookieName')}
                  value={cookie.key}
                  onChange={(e) => handleCookieKeyChange(index, e.target.value)}
                  showDynamicVarPicker={false}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
              renderValue={
                <SyntaxHighlightInput
                  selectionToVariable
                  placeholder={t('request.cookieValue')}
                  value={cookie.value}
                  onChange={(e) => updateCookies(index, 'value', e.target.value)}
                  className={REQUEST_BUILDER_KV_INPUT_CLASS}
                  style={REQUEST_BUILDER_KV_INPUT_STYLE}
                  selectedEnvironment={selectedEnvironment}
                  workspaceVariables={workspaceVariables}
                  globalVariables={globalVariables}
                  collectionVariables={collectionVariables}
                  folderVariables={folderVariables}
                  onVariableUpdate={onVariableUpdate}
                  variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                />
              }
            />
          ))}
        </KvTable>
      )}
      {/* Script Tab — Pre / Post sub-tabs */}
      {activeTab === 'script' && (
        <div className="script-tab-shell px-5 pt-0 pb-0">
          <nav className="script-subtab-bar flex space-x-1 flex-shrink-0">
            {scriptSubTabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setScriptSubTab(key)}
                className={`py-0.5 px-2.5 font-medium text-[11px] rounded bg-transparent transition-colors duration-150 ${
                  scriptSubTab === key
                    ? 'text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="script-editor-shell">
            {scriptSubTab === 'pre' ? (
              <CodeEditor
                splitLayoutTick={splitLayoutTick}
                hideChrome
                value={preRequestScript}
                onChange={(val) => {
                  setPreRequestScript(val);
                  notifyChange({ preRequestScript: val });
                }}
                language="javascript"
                placeholder={t('request.preRequestPlaceholder')}
                height="100%"
                onCtrlEnter={sendHttpRequest}
              />
            ) : (
              <CodeEditor
                splitLayoutTick={splitLayoutTick}
                hideChrome
                value={testScript}
                onChange={(val) => {
                  setTestScript(val);
                  notifyChange({ testScript: val });
                }}
                language="javascript"
                placeholder={t('request.postResponsePlaceholder')}
                height="100%"
                onCtrlEnter={sendHttpRequest}
              />
            )}
          </div>
        </div>
      )}
    </RequestTabBody>
  );
}
